import { useEffect, useMemo, useState } from 'react';
import { apiService, ContainerApplyEntry, ContainerApplyResult, PremiumData } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { useSnapshotDraft, getDraftPayload, draftKey } from '@/stores/unsavedDrafts.store';
import {
  ApplyReport, ContainerScope, ScopeEntry, applyResultOf, useContainersData, useProjectContainers,
} from '@/components/config/AllContainers';
import styles from '@/pages/GradesPage.module.scss';

const NUM_FIELDS: [string, string][] = [
  // Nombre de loots des BOX à clés (PremiumMod/LootBox) ; les lootbox sans clé n'en ont pas par grade.
  ['LootCount', 'Nombre de loots (box)'],
  ['IdleRewardSecond', 'Idle reward (s)'],
  ['ReducerLoseExperience', 'Réduction perte XP mort (%)'],
  ['AmountEnderChestLockedSlot', 'Slots enderchest verrouillés'],
  ['ViewDistance', 'Distance de vue'],
  ['RtpDailyLimit', 'Limite RTP / jour'],
  ['BonusXpJobsPercent', 'Bonus XP jobs (%)'],
  ['ExpirationTimeDeathLocate', 'Death locate (ms)'],
  ['MaxCanFeedDistance', 'Distance max feed'],
];
const LIST_FIELDS: [string, string, string][] = [
  ['RewardIds', 'Reward IDs (weekly)', 'ex. divinite_key, paladin_key'],
  ['Kits', 'Kits', 'ex. divinite_kit, legende_kit'],
];
const BOOL_FIELDS: [string, string][] = [
  ['CanFeedOtherPlayers', 'Peut nourrir les autres'],
  ['CanFeedSelf', 'Peut se nourrir'],
  ['HasFeedDistanceLimit', 'Limite de distance de feed'],
];

/** Libellé de chaque champ, pour dire sur quoi un container diffère. */
const LABELS: Record<string, string> = Object.fromEntries(
  [...NUM_FIELDS, ...LIST_FIELDS, ...BOOL_FIELDS].map(([key, label]) => [key, label]),
);

const NOT_FOUND: PremiumData = { found: false, file: null, groups: [], nametags: [], ranktags: [] };

interface BoutiqueForm { form: Record<string, string>; bools: Record<string, boolean> }

/** Brouillon d'un grade : le formulaire, et `retry` quand un container n'a pas reçu le dernier enregistrement. */
interface BoutiqueDraft extends BoutiqueForm { retry?: boolean }

/** Valeurs d'un grade dans la config premium d'un container, au format du formulaire. */
function formOf(data: PremiumData | undefined, grade: string): BoutiqueForm {
  const v = (data?.groups.find((x) => x.name === grade)?.values ?? {}) as Record<string, unknown>;
  const form: Record<string, string> = {};
  for (const [key] of NUM_FIELDS) form[key] = v[key] != null ? String(v[key]) : '';
  for (const [key] of LIST_FIELDS) form[key] = Array.isArray(v[key]) ? (v[key] as unknown[]).join(', ') : '';
  const bools: Record<string, boolean> = {};
  for (const [key] of BOOL_FIELDS) bools[key] = !!v[key];
  return { form, bools };
}

function differingFields(a: BoutiqueForm, b: BoutiqueForm): string[] {
  const out: string[] = [];
  for (const [key] of [...NUM_FIELDS, ...LIST_FIELDS]) if ((a.form[key] ?? '') !== (b.form[key] ?? '')) out.push(key);
  for (const [key] of BOOL_FIELDS) if (!!a.bools[key] !== !!b.bools[key]) out.push(key);
  return out;
}

/**
 * Boutique d'un grade (Supervision > Grades, onglet Boutique) : config premium (kits, rewards, RTP, loots…). Commune à tous les
 * containers du projet : lue dans le premier container (par id) qui porte la config premium,
 * enregistrée (barre du bas) dans chaque container qui la porte.
 */
export function GradeBoutique({ grade, readOnly = false }: { grade: string; readOnly?: boolean }) {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const { containers, loaded } = useProjectContainers();
  const [reload, setReload] = useState(0);
  const { data: all } = useContainersData(
    containers,
    (id) => apiService.getContainerPremium(id).then((r) => r.data ?? NOT_FOUND),
    reload,
  );
  const [form, setForm] = useState<Record<string, string>>({});
  const [bools, setBools] = useState<Record<string, boolean>>({});
  const [orig, setOrig] = useState('');
  /**
   * Dernier enregistrement en échec sur au moins un container. La référence, elle, a pu être écrite :
   * relue, elle égale le formulaire, et sans ce drapeau le brouillon quitterait la barre alors qu'un
   * container porte encore les anciennes valeurs.
   */
  const [retry, setRetry] = useState(false);
  const [report, setReport] = useState<ContainerApplyResult | null>(null);

  /** Référence : le premier container (ordre stable) qui porte la config premium. */
  const ref = loaded && all ? containers.find((c) => all[c.id]?.found) : undefined;
  const refForm = useMemo(() => (ref && all ? formOf(all[ref.id], grade) : null), [all, ref, grade]);
  const key = draftKey('grade-boutique', activeGameId, grade);

  // Valeurs de la référence, ou le brouillon en attente (retour d'onglet ou de grade).
  useEffect(() => {
    if (!refForm) return;
    setOrig(JSON.stringify({ f: refForm.form, b: refForm.bools }));
    const pending = getDraftPayload(key) as BoutiqueDraft | undefined;
    setRetry(!!pending?.retry);
    setForm(pending?.form ?? refForm.form);
    setBools(pending?.bools ?? refForm.bools);
  }, [refForm, key]);

  const buildValues = (): Record<string, unknown> => {
    const values: Record<string, unknown> = {};
    // Un nombre vidé part à null : le serveur retire le champ, et le container s'aligne aussi là
    // où la référence n'en porte pas.
    for (const [key] of NUM_FIELDS) {
      const raw = (form[key] ?? '').trim();
      values[key] = raw === '' ? null : Number(raw);
    }
    for (const [key] of LIST_FIELDS) values[key] = (form[key] ?? '').split(',').map((s) => s.trim()).filter(Boolean);
    for (const [key] of BOOL_FIELDS) values[key] = !!bools[key];
    return values;
  };

  const dirty = !readOnly && !!ref && (retry || JSON.stringify({ f: form, b: bools }) !== orig);

  const scope: ScopeEntry[] = containers.map((c) => {
    const d = all?.[c.id];
    if (!d?.found || !refForm) return { containerId: c.id, containerName: c.name, status: 'SKIPPED_FILE_MISSING' };
    if (c.id === ref?.id) return { containerId: c.id, containerName: c.name, status: 'REFERENCE' };
    const differs = differingFields(formOf(d, grade), refForm);
    return { containerId: c.id, containerName: c.name, status: differs.length ? 'DIFFERENT' : 'SAME', differs };
  });

  /** Écrit la config boutique du grade dans chaque container du projet qui porte la config premium. */
  const save = async () => {
    const values = buildValues();
    const edited: BoutiqueForm = { form, bools };
    const entries: ContainerApplyEntry[] = [];
    for (const c of containers) {
      const d = all?.[c.id];
      const base = { containerId: c.id, containerName: c.name, path: d?.file ?? null };
      if (!d?.found) {
        entries.push({ ...base, status: 'SKIPPED_FILE_MISSING', message: 'Config premium introuvable (container non synchronisé ?).' });
        continue;
      }
      if (differingFields(formOf(d, grade), edited).length === 0) {
        entries.push({ ...base, status: 'UNCHANGED', message: 'Déjà identique.' });
        continue;
      }
      const res = await apiService.saveContainerPremiumGroup(c.id, grade, values);
      entries.push(res.data?.success
        ? { ...base, status: 'WRITTEN', message: `Grade ${grade}` }
        : { ...base, status: 'FAILED', message: res.data?.message ?? res.error ?? 'Enregistrement en échec' });
    }
    const result = applyResultOf(entries);
    setReport(result);
    if (result.success) setOrig(JSON.stringify({ f: form, b: bools }));
    setRetry(!result.success);
    setReload((k) => k + 1);
    return result.success;
  };

  useSnapshotDraft({
    kind: 'grade-boutique',
    groupId: activeGameId,
    identifier: grade,
    label: `Boutique · ${grade} · tous les containers`,
    dirty,
    payload: { form, bools, retry },
    save,
  });

  if (loaded && containers.length === 0) {
    return (
      <p className={styles.muted}>
        Aucun container pour ce projet : déclare-en un dans <strong>Paramètres généraux → Containers</strong> puis synchronise-le.
      </p>
    );
  }

  return (
    <div className={styles.essBlock}>
      {!loaded || !all ? (
        <div className={styles.loadingState}><div className={styles.spinner} /><span>Chargement…</span></div>
      ) : !ref ? (
        <p className={styles.muted}>
          Config premium introuvable dans les containers du projet : synchronise-les dans Paramètres généraux → Containers.
        </p>
      ) : (
        <>
          <ContainerScope entries={scope} referencePath={all[ref.id]?.file} labelOf={(k) => LABELS[k] ?? k} />
          {LIST_FIELDS.map(([key, label, ph]) => (
            <div key={key} className={styles.essField}>
              <label className={styles.essLabel}>{label}</label>
              <input className={styles.input} value={form[key] ?? ''} disabled={readOnly} placeholder={ph} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
            </div>
          ))}

          {NUM_FIELDS.map(([key, label]) => (
            <div key={key} className={styles.essField}>
              <label className={styles.essLabel}>{label}</label>
              <input className={styles.input} type="number" value={form[key] ?? ''} disabled={readOnly} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
            </div>
          ))}

          {BOOL_FIELDS.map(([key, label]) => (
            <label key={key} className={styles.essLabel} style={{ cursor: readOnly ? 'default' : 'pointer', gap: 8 }}>
              <input type="checkbox" checked={!!bools[key]} disabled={readOnly} onChange={(e) => setBools((b) => ({ ...b, [key]: e.target.checked }))} /> {label}
            </label>
          ))}

          {report && <ApplyReport result={report} onClose={() => setReport(null)} />}

          <p className={styles.muted}>
            {retry
              ? "Un container n'a pas reçu le dernier enregistrement : enregistre à nouveau via la barre du bas."
              : dirty
                ? 'Modifications en attente : enregistre-les via la barre du bas, elles seront écrites dans tous les containers.'
                : 'Enregistré.'} Envoie ensuite les containers avec le bouton Envoyer.
          </p>
        </>
      )}
    </div>
  );
}
